import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'emotion-theming';
import {
  trackingPageColors as defaultColors,
  trackingPageSettings as defaultSettings,
} from '@sendwise/default-settings';

import { FontLoader } from '@sendwise/font-loader';

import Banner from '../widgets/Banner';
import Header from '../widgets/Header';
import Footer from '../widgets/Footer';
import Status from '../widgets/Status';
import Products from '../widgets/Products';
import ContainerResizeListener from '../components/ContainerResizeListener';
import DummyBanner from '../components/DummyBanner';
import PageContext from './PageContext';

import styles from './TrackingPage.module.scss';

const widgetMapping = {
  banner: Banner,
  header: Header,
  footer: Footer,
  status: Status,
  products: Products,
};

class TrackingPage extends React.PureComponent {
  state = {
    isMobile: false,
  };

  handleResize = ({ width }) => {
    this.setState({
      isMobile: width <= 768,
      isWideScreen: width > 1170,
    });
  };

  getDesignSettings() {
    return {
      ...defaultColors,
      ...this.props.designSettings,
    };
  }

  getTheme() {
    const {
      bodyFont,
      bodyFontSize,
    } = this.getDesignSettings();

    return {
      bodyFont,
      bodyFontSize,
    };
  }

  renderWidget = (widget, options) => {
    const {
      isMobile,
      isWideScreen,
    } = this.state;

    if (!widget) {
      return null;
    }

    const WidgetComponent = widgetMapping[widget.type];

    const widgetSettings = {
      ...defaultSettings[widget.type],
      ...widget.settings,
    };

    if (WidgetComponent) {
      return (
        <div id={widget.id} key={widget.id} styleName="widget-wrapper">{/* wrapper with id necessary for auto-scroll */}
          <WidgetComponent
            isPreview={this.props.isPreview}
            isMobile={isMobile}
            isWideScreen={isWideScreen}
            {...widgetSettings}
            designSettings={this.getDesignSettings()}
            {...options}
          />
        </div>
      );
    }

    return null;
  };

  renderBody(widgets) {
    const {
      bodyBackgroundColor: backgroundColor,
    } = this.props.designSettings;

    return (
      <div key="body" className={styles.body} style={ { backgroundColor } }>
        { widgets.map(this.renderWidget) }
      </div>
    );
  }

  render() {
    const {
      onChangeLanguage,
    } = this.props;

    const {
      header,
      body,
      footer,
    } = splitTrackingPageWidgets(this.props.widgets);

    return (
      <ThemeProvider theme={this.getTheme()}>
        <PageContext.Provider value={this.state}>
          <ContainerResizeListener styleName="page-container" id="page-container" onResize={this.handleResize}>
            { this.props.isDummy && <DummyBanner isMobile={this.state.isMobile} /> }
            { this.renderWidget(header, { onChangeLanguage }) }
            { this.renderBody(body) }
            { this.renderWidget(footer) }
          </ContainerResizeListener>
          <FontLoader designSettings={this.props.designSettings} />
        </PageContext.Provider>
      </ThemeProvider>
    );
  }
}

function splitTrackingPageWidgets(widgets) {
  return {
    header: widgets.find(w => w.type === 'header'),
    body: widgets.filter(w => w.type !== 'header' && w.type !== 'footer'),
    footer: widgets.find(w => w.type === 'footer'),
  };
}

TrackingPage.defaultProps = {
  widgets: [],
};

TrackingPage.propTypes = {
  isPreview: PropTypes.bool,
  widgets: PropTypes.arrayOf(PropTypes.object),
  designSettings: PropTypes.object.isRequired,
};

export default TrackingPage;
